import React, { useEffect, useRef } from 'react'
import { constants } from '../../constants'

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * Cloudflare Turnstile widget.
 *
 * Mirrors the patient app's implementation (physio_app_web
 * components/login/screen6.js) — explicit render, no npm package, one script
 * tag shared by every widget on the page.
 *
 * Replaces `react-google-recaptcha`, which the admin used on two public forms
 * with a site key that was malformed in env-config (39 chars; Google's are 40).
 * An invalid key never initialises, so `executeAsync()` never resolved and both
 * forms hung on submit with nothing shown to the user.
 *
 * Unlike invisible reCAPTCHA there is no "execute on submit" call: the widget
 * renders on mount and hands back a token via `onToken`. Callers gate their
 * submit on holding one. Tokens are SINGLE USE — call `reset()` (exposed on
 * `resetRef`) after every submission or the next attempt is rejected server-side.
 *
 * @param {(token: string|null) => void} onToken  fires with the token, or null
 *                                                when it expires / errors
 * @param {object} resetRef  ref that receives a `.current` reset function
 * @param {object} tokenRef  ref that receives a `.current` getter returning the
 *                           widget's current token, read straight from
 *                           Turnstile. Callers should prefer their state but
 *                           fall back to this: the callback is the one link in
 *                           the chain we do not control, and if it does not
 *                           fire the user is stuck with no way to submit and no
 *                           error explaining why.
 */
const TurnstileWidget = ({ onToken, resetRef, tokenRef }) => {
  const container = useRef(null)
  const widgetId = useRef(null)
  // Held in a ref so the render effect never needs onToken in its deps — a new
  // inline callback each parent render would otherwise re-render the widget and
  // silently drop the token the user already solved for.
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  useEffect(() => {
    let cancelled = false

    const render = () => {
      if (
        cancelled ||
        typeof window === 'undefined' ||
        !window.turnstile ||
        !container.current ||
        widgetId.current !== null
      )
        return
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: constants.TURNSTILE_SITE_KEY,
        callback: (token) => onTokenRef.current && onTokenRef.current(token),
        'expired-callback': () =>
          onTokenRef.current && onTokenRef.current(null),
        'error-callback': (code) => {
          // Surface the code — Turnstile renders a generic "Troubleshoot" link
          // and the reason is otherwise invisible. 110200 = domain not allowed,
          // 110100/110110 = bad sitekey.
          console.error('Turnstile error-callback:', code)
          onTokenRef.current && onTokenRef.current(null)
        },
      })
      if (tokenRef) {
        tokenRef.current = () => {
          try {
            return window.turnstile && widgetId.current !== null
              ? window.turnstile.getResponse(widgetId.current)
              : null
          } catch (e) {
            return null
          }
        }
      }
      if (resetRef) {
        resetRef.current = () => {
          if (window.turnstile && widgetId.current !== null) {
            window.turnstile.reset(widgetId.current)
          }
          onTokenRef.current && onTokenRef.current(null)
        }
      }
    }

    if (!constants.TURNSTILE_SITE_KEY) {
      // Loud on purpose. Without this the form renders normally and fails only
      // at submit, which is exactly how the reCAPTCHA breakage stayed hidden.
      console.error(
        'REACT_APP_TURNSTILE_SITE_KEY not set - captcha cannot render, submissions will be rejected'
      )
      return
    }

    if (window.turnstile) {
      render()
      return
    }

    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', render)
      return () => {
        cancelled = true
        existing.removeEventListener('load', render)
      }
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = render
    script.onerror = () =>
      console.error('Failed to load Cloudflare Turnstile script')
    document.head.appendChild(script)

    return () => {
      cancelled = true
    }
  }, [resetRef])

  return <div ref={container} />
}

export default TurnstileWidget
